import { useState } from 'react'

import { Alert, Box, Icon, Table, Text } from '@factorialco/deprecated-design-system'
import { GetPayrollSummarySampleDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  LabelMappedWithAI,
  LabelToConceptId
} from 'modules/payroll_bundle/components/PayrollSummaryImport/PayrollSummaryImportModal/ImportSummaryWizard/types'
import { useAutomapper } from 'modules/payroll_bundle/hooks/useAutomapper'
import { getConceptByName, useConcepts } from 'modules/payroll_bundle/hooks/useConcepts'
import { useResultLabelConceptMappings } from 'modules/payroll_bundle/hooks/useResultLabelConceptMappings'
import type { ResultLabelConceptMapping } from 'modules/payroll_bundle/hooks/useResultLabelConceptMappings'
import type { ResultLabelConceptMappingSuggestion } from 'modules/payroll_bundle/hooks/useResultLabelConceptMappingsSuggestions'
import { DEFAULT_RESULT_CONCEPT } from 'modules/payroll_bundle/lib/constants'

import ConceptsGroup, { conceptLine } from './ConceptsGroup'
import PersonalDataGroup from './PersonalDataGroup'
import TableLoading from './_TableLoading'

// This component is a copy of a similar one created some time ago. If someone want to checks the
// blame of the file, please check this file
// https://github.com/factorialco/factorial/blob/ea3fa5b19eab385a8aab2b636193c6ba0baa50d8/frontend/src/modules/payroll_bundle/pages/ResultsAndPaymentsPage/ResultsPage/MapToConcepts/index.tsx

export interface State {
  labelToConceptId: LabelToConceptId
  labelMappedWithAI: LabelMappedWithAI
}

const orderLinesByPreviousMappingsAndNetPay = (
  resultLabelConceptMappings: ResultLabelConceptMapping[],
  lines: conceptLine[] | undefined
) => {
  // Map with concepts ids as keys and labels as values
  const mappedConcepts = new Map()

  resultLabelConceptMappings
    // Exclude previous net pay mappings, the net pay selected from step 1 takes precedence
    .filter((item) => item.conceptId !== null)
    .forEach((item) => mappedConcepts.set(item.conceptId, item.label))

  // Sort to the top by checking the concept id and field name
  // net pay will be also moved to the top
  lines?.sort((a, b) => {
    // First shows the net salary
    if (a.type === 'net_salary') return -1
    if (b.type === 'net_salary') return 1

    // Then shows the mapped concepts
    if (mappedConcepts.has(a.conceptId) && mappedConcepts.has(b.conceptId)) return 0
    if (mappedConcepts.has(a.conceptId)) return -1
    if (mappedConcepts.has(b.conceptId)) return 1

    return 0
  })
}

// Returns an object with the `lines` labels mapped to the same concepts as
// in `resultLabelConceptMappings`, or to undefined for labels that have no
// mapping in `resultLabelConceptMappings`
const buildLabelToConceptId = (
  netSalaryConceptId: number | undefined,
  lines: conceptLine[],
  resultLabelConceptMappings: ResultLabelConceptMapping[]
) => {
  return lines?.reduce((acc, line) => {
    const isNetSalaryColumnFromStep1 = line.type === 'net_salary' && netSalaryConceptId
    const lineMapping = resultLabelConceptMappings.find((m) => m.conceptId === line.conceptId)
    const isNetSalaryMapping = lineMapping?.conceptId === netSalaryConceptId

    // By default nothing is selected
    let conceptId = null
    // If the line was the selected as net salary on the step 1 of the import
    // it is selected as net salary on the step 2
    if (isNetSalaryColumnFromStep1) {
      conceptId = netSalaryConceptId
    }
    // If the mapped salary is not net salary (So we avoid a dupplicated net salary mapping)
    // we use the mapped concept
    else if (!isNetSalaryMapping) {
      conceptId = lineMapping?.conceptId
    }

    return {
      ...acc,
      [line.fieldName]: conceptId
    }
  }, {})
}

const updateLabelToConceptId = (
  labelToConceptId: LabelToConceptId,
  labelConceptMappingsSuggestions: ResultLabelConceptMappingSuggestion[]
) => {
  const labels = new Set(Object.keys(labelToConceptId))
  return labelConceptMappingsSuggestions.reduce((updatedLabelToConceptId, mappingSuggestion) => {
    // Ignores suggestion if there's no suggestion, label doesn't exist, or it's already mapped
    if (
      !mappingSuggestion.conceptId ||
      !labels.has(mappingSuggestion.label) ||
      labelToConceptId[mappingSuggestion.label]
    ) {
      return updatedLabelToConceptId
    }
    return { ...updatedLabelToConceptId, [mappingSuggestion.label]: mappingSuggestion.conceptId }
  }, labelToConceptId)
}

const updateLabelToMappedWithAI = (
  labelMappedWithAI: LabelMappedWithAI,
  labelConceptMappingsSuggestions: ResultLabelConceptMappingSuggestion[]
) => {
  const labels = new Set(Object.keys(labelMappedWithAI))
  return labelConceptMappingsSuggestions.reduce((updatedLabelToMappedWithAI, mappingSuggestion) => {
    if (
      !mappingSuggestion.conceptId ||
      !labels.has(mappingSuggestion.label) ||
      labelMappedWithAI[mappingSuggestion.label]
    ) {
      return updatedLabelToMappedWithAI
    }
    return { ...updatedLabelToMappedWithAI, [mappingSuggestion.label]: true }
  }, labelMappedWithAI)
}

interface Props {
  currency: string
  payrollSummaryImportId: number
  onUpdateLabelToConcept: (labelToConceptId: LabelToConceptId) => void
  initialLabelToConceptId?: LabelToConceptId
  onLoading?: (loading: boolean) => void
}

const MapToConcepts = ({
  currency,
  payrollSummaryImportId,
  onUpdateLabelToConcept,
  initialLabelToConceptId,
  onLoading
}: Props) => {
  const {
    company: { id: companyId }
  } = useCurrentSession()

  const [firstTimeMapping, setFirstTimeMapping] = useState<boolean>(true)
  const [automappingCancelled, setAutomappingCancelled] = useState<boolean>()
  const [labelToConceptId, setLabelToConceptId] = useState<LabelToConceptId>(
    initialLabelToConceptId || {}
  )
  const [labelMappedWithAI, setLabelMappedWithAI] = useState<LabelMappedWithAI>({})

  const { concepts, loading: loadingConcepts } = useConcepts()

  const { resultLabelConceptMappings, loading: loadingResultLabelConceptMappings } =
    useResultLabelConceptMappings()

  const {
    accumulatedSuggestions,
    fetchingSuggestions,
    autoMapLabels: autoMapLabelsv2
  } = useAutomapper(true)

  const { loading: isLoadingSamples, data: importSamples } = useQuery(
    GetPayrollSummarySampleDocument,
    {
      variables: {
        financePayrollSummaryImportId: Number(payrollSummaryImportId)
      },
      fetchPolicy: 'no-cache'
    }
  )

  const importSample = importSamples?.payroll.payrollSummarySamplesConnection.nodes || []
  const conceptList = concepts

  const lines = importSample
    .filter((concept) => concept.type == null || concept.type == 'net_salary')
    .filter((line) => !isNaN(Number(line.sample)) && line.type !== 'id')

  const netSalaryConcept = getConceptByName(concepts, DEFAULT_RESULT_CONCEPT.net_pay)

  orderLinesByPreviousMappingsAndNetPay(resultLabelConceptMappings, lines)

  const firstName = importSample.find((concept) => concept.type == 'first_name')?.sample
  const lastName = importSample.find((concept) => concept.type == 'last_name')?.sample
  const fullName = importSample.find((concept) => concept.type == 'full_name')?.sample
  const identifier = importSample.find((concept) => concept.type == 'identifier')?.sample
  const companyIdentifier = importSample.find(
    (concept) => concept.type == 'company_identifier'
  )?.sample

  const isTableLoading = !automappingCancelled && fetchingSuggestions
  const onCancelTableLoading = () => {
    setAutomappingCancelled(true)
  }

  useEffect(() => {
    onLoading?.(!automappingCancelled && fetchingSuggestions)
  }, [fetchingSuggestions, automappingCancelled])

  const hasMappedConcepts = Object.values(labelMappedWithAI).some((mapped) => mapped)

  useEffect(
    () => {
      if (lines === undefined) return
      if (loadingResultLabelConceptMappings || isLoadingSamples) return

      if (hasMappedConcepts) return

      const newLabelToConceptId = buildLabelToConceptId(
        netSalaryConcept?.id,
        lines,
        resultLabelConceptMappings
      )

      const newLabelMappedWithAI = Object.keys(newLabelToConceptId).reduce(
        (acc, label) => ({
          ...acc,
          [label]: false
        }),
        {}
      )
      setLabelToConceptId(newLabelToConceptId)
      setLabelMappedWithAI(newLabelMappedWithAI)
      onUpdateLabelToConceptId(newLabelToConceptId)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lines.length, loadingResultLabelConceptMappings, isLoadingSamples]
  )

  useUnsafeEffect(() => {
    if (!labelToConceptId || Object.keys(labelToConceptId).length === 0) return
    if (!firstTimeMapping) return

    if (!hasMappedConcepts) {
      autoMapLabelsv2(companyId, labelToConceptId, conceptList)
    }

    setFirstTimeMapping(false)
  }, [labelToConceptId])

  useUnsafeEffect(() => {
    if (accumulatedSuggestions.length === 0) return

    const updatedLabelToConceptId = updateLabelToConceptId(labelToConceptId, accumulatedSuggestions)

    const updatedLabelMappedWithAI = updateLabelToMappedWithAI(
      labelMappedWithAI,
      accumulatedSuggestions
    )

    setLabelToConceptId(updatedLabelToConceptId)
    onUpdateLabelToConcept(updatedLabelToConceptId)
    setLabelMappedWithAI(updatedLabelMappedWithAI)
    onLoading?.(false)
  }, [accumulatedSuggestions])

  const onUpdateLabelToConceptId = (labelToConceptId: LabelToConceptId) => {
    setLabelToConceptId(labelToConceptId)
    onUpdateLabelToConcept(labelToConceptId)
  }

  const onUpdateLabelMappedWithAI = (labelMappedWithAI: LabelMappedWithAI) => {
    setLabelMappedWithAI(labelMappedWithAI)
  }

  if (loadingConcepts || loadingResultLabelConceptMappings || isLoadingSamples) {
    return <Loading />
  }

  return (
    <>
      <Box width="full">
        <Alert color="info">
          <Box flexNoShrink>
            <Icon.Medium icon="ai" color="viridian1000" />
          </Box>
          <Text.Normal>
            {i18n.t(
              'payroll_bundle.results_payments.result_upload.map_to_concepts.auto_mapping.banner'
            )}
          </Text.Normal>
        </Alert>
      </Box>
      <Box gap="s0" overflowY="auto">
        <Table autoLayout fullWidth>
          <Table noRadius hideBorder>
            <Table.Head>
              <Table.HeadCell>
                {i18n.t(
                  'payroll_bundle.results_payments.result_upload.map_to_concepts.data_in_file'
                )}
              </Table.HeadCell>
              <Table.HeadCell>
                {i18n.t('payroll_bundle.results_payments.result_upload.map_to_concepts.sample')}
              </Table.HeadCell>
              <Table.HeadCell>
                {i18n.t(
                  'payroll_bundle.results_payments.result_upload.map_to_concepts.data_in_factorial'
                )}
              </Table.HeadCell>
            </Table.Head>
          </Table>
          <Table noRadius hideBorder>
            {isTableLoading && <TableLoading colSpan={3} onCancelLoading={onCancelTableLoading} />}
            {!isTableLoading && (
              <>
                {(firstName || fullName || identifier || companyIdentifier) && (
                  <PersonalDataGroup
                    firstName={firstName}
                    lastName={lastName}
                    fullName={fullName}
                    identifier={identifier}
                    companyIdentifier={companyIdentifier}
                  />
                )}
                {lines && (
                  <ConceptsGroup
                    currency={currency}
                    lines={lines}
                    concepts={conceptList}
                    labelToConceptId={labelToConceptId}
                    labelMappedWithAI={labelMappedWithAI}
                    onUpdateLabelToConceptId={onUpdateLabelToConceptId}
                    onUpdateLabelMappedWithAI={onUpdateLabelMappedWithAI}
                  />
                )}
              </>
            )}
          </Table>
        </Table>
      </Box>
    </>
  )
}

export default MapToConcepts
